import { Fragment, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Copy, Info } from 'lucide-react';
import {
  SAMPLE_LANG_LABELS,
  type CodeSample,
  type GuideBlock,
  type SampleLang,
} from './guideContent';

// `code`, **bold** and [label](url): the only inline markup the guide uses.
const INLINE = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

export function InlineText({ text }: { text: string }) {
  return (
    <>
      {text.split(INLINE).map((part, index) => {
        if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
          return (
            <code
              key={index}
              className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[0.85em] font-mono text-gray-800 dark:text-gray-100 break-words"
            >
              {part.slice(1, -1)}
            </code>
          );
        }
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (link) {
          return (
            <a
              key={index}
              href={link[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              {link[1]}
            </a>
          );
        }
        return <Fragment key={index}>{part}</Fragment>;
      })}
    </>
  );
}

export function CopyButton({
  value,
  label,
  className = '',
}: {
  value: string;
  /** What is being copied, for the accessible name, e.g. "Base URL". */
  label: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      title={copied ? 'Copied' : 'Copy'}
      className={`inline-flex items-center justify-center p-1.5 rounded ${
        copied
          ? 'text-green-600 dark:text-green-400'
          : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
      } ${className}`}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export function CodeBlock({
  samples,
  lang,
  onLangChange,
}: {
  samples: CodeSample[];
  /** The reader's preferred language; used when this block offers it. */
  lang: SampleLang;
  onLangChange: (lang: SampleLang) => void;
}) {
  const active = samples.find((sample) => sample.lang === lang) ?? samples[0];
  if (!active) return null;
  const hasTabs = samples.length > 1;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 pl-2 pr-1">
        {hasTabs ? (
          <div role="tablist" aria-label="Code sample language" className="flex">
            {samples.map((sample) => {
              const selected = sample.lang === active.lang;
              return (
                <button
                  key={sample.lang}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => onLangChange(sample.lang)}
                  className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
                    selected
                      ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  {SAMPLE_LANG_LABELS[sample.lang]}
                </button>
              );
            })}
          </div>
        ) : (
          <span className="px-1 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
            {SAMPLE_LANG_LABELS[active.lang]}
          </span>
        )}
        <CopyButton value={active.code} label={`${SAMPLE_LANG_LABELS[active.lang]} sample`} />
      </div>
      <pre className="p-4 overflow-x-auto text-xs leading-relaxed bg-gray-900 text-gray-100">
        <code>{active.code}</code>
      </pre>
    </div>
  );
}

function GuideTable({ block }: { block: Extract<GuideBlock, { type: 'table' }> }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
          <tr>
            {block.head.map((cell) => (
              <th key={cell} scope="col" className="px-3 py-2 font-medium">
                <InlineText text={cell} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {block.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="align-top">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  {block.copyColumn === cellIndex ? (
                    <span className="inline-flex items-center gap-1">
                      <code className="font-mono text-xs text-gray-900 dark:text-gray-100 break-all">
                        {cell}
                      </code>
                      <CopyButton value={cell} label={row[0] ?? 'value'} />
                    </span>
                  ) : (
                    <InlineText text={cell} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function GuideBlockView({
  block,
  lang,
  onLangChange,
  checklist,
}: {
  block: GuideBlock;
  lang: SampleLang;
  onLangChange: (lang: SampleLang) => void;
  /** Rendered in place of the `checklist` block. */
  checklist: ReactNode;
}) {
  switch (block.type) {
    case 'p':
      return (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <InlineText text={block.text} />
        </p>
      );
    case 'heading':
      return (
        <h4 className="pt-2 text-sm font-semibold text-gray-900 dark:text-white">{block.text}</h4>
      );
    case 'list': {
      const ListTag = block.ordered ? 'ol' : 'ul';
      return (
        <ListTag
          className={`${block.ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-300`}
        >
          {block.items.map((item, index) => (
            <li key={index}>
              <InlineText text={item} />
            </li>
          ))}
        </ListTag>
      );
    }
    case 'note': {
      const warning = block.tone === 'warning';
      const Icon = warning ? AlertTriangle : Info;
      return (
        <div
          className={`flex gap-2 rounded-lg border p-3 text-sm ${
            warning
              ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200'
              : 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
          }`}
        >
          <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
          <p>
            <InlineText text={block.text} />
          </p>
        </div>
      );
    }
    case 'table':
      return <GuideTable block={block} />;
    case 'code':
      return <CodeBlock samples={block.samples} lang={lang} onLangChange={onLangChange} />;
    case 'checklist':
      return <>{checklist}</>;
  }
}
